import { useEffect } from 'react'
import BottomSheetContent from '../../../components/BottomSheetComponents/BottomSheetContent'
import { GROUPED_STATIC_TEXT_ROLE } from '../../../components/ui-components/NameValueTableRow'
import type { TaxHarvestingSummaryResponse } from '../types/summary.types'
import { GainType } from '../types/summary.types'
import { roundOffAndFormatNumberWithCurrency } from '../../../utils/utils'
import { NoTaxSavings } from '../components/NoTaxSavings'
import { useBottomSheet } from '../../../state-management/contexts/BottomSheetContext'
import { TaxSVG } from '../components/TaxSVG'
import { InfoIcon } from '../../../icons/rebalance/InfoIcon'
import {
  TaxHarvestingScreenName,
  TaxHarvestingBottomSheetName,
  TaxHarvestingEventId,
  trackTaxHarvestingBottomSheetLoaded,
  trackTaxHarvestingUserClicked
} from '../tracking/taxHarvestingTracking'
import { RealisedStclInfoSheet } from './StcgBottomSheet'

type ViewType = 'summary' | 'tax'

type Props = {
  onDismiss: () => void
  view: ViewType
  summary?: TaxHarvestingSummaryResponse
  longTermGainsSummary?: TaxHarvestingSummaryResponse['longTermGainsSummary']
}

export default function LtcgBottomSheet({ onDismiss, view, summary, longTermGainsSummary }: Props) {
  const isSummary = view === 'summary'
  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(
      isSummary ? TaxHarvestingBottomSheetName.LtcgSummary : TaxHarvestingBottomSheetName.LtcgTaxLiability
    )
  }, [isSummary])
  return (
    <BottomSheetContent
      heading={isSummary ? 'LTCG Summary' : 'Tax Liability (LTCG)'}
      bottomSheetHeadingClassName={isSummary ? '!mb-0' : ''}
      onDismiss={onDismiss}
    >
      {isSummary && <p className='mb-4 text-sm text-grey40'>Gains from long-term investments sold this FY</p>}
      {isSummary ? <LtcgSummary summary={summary} longTermGainsSummary={longTermGainsSummary} /> : <LtcgTaxLiability summary={summary} longTermGainsSummary={longTermGainsSummary} />}
    </BottomSheetContent>
  )
}

export const LtcgSectionRow = ({
  label,
  value,
  helper,
  highlight,
  onClick,
  showInfo
}: {
  label: string
  value: string
  helper?: string
  highlight?: boolean
  onClick?: () => void
  showInfo?: boolean
}) => (
  <div
    className={`flex items-start justify-between text-sm py-2 ${onClick ? 'cursor-pointer' : ''}`}
    onClick={onClick}
    role={onClick ? 'button' : GROUPED_STATIC_TEXT_ROLE}
    tabIndex={onClick ? 0 : undefined}
    onKeyDown={onClick ? (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        onClick()
      }
    } : undefined}
    aria-label={`${label}${helper ? `, ${helper}` : ''}, ${value}`}
  >
    <div className='flex flex-col gap-[2px]' aria-hidden='true'>
      <div className='flex items-center gap-1'>
        <span className='text-[#6A6E6E]'>{label}</span>
        {showInfo && (
          <span className='inline-flex items-center justify-center'>
            <InfoIcon />
          </span>
        )}
      </div>
      {helper && <span className='text-sm text-[#8E9393]'>{helper}</span>}
    </div>
    <span aria-hidden='true' className={`font-medium ${highlight ? 'text-[#C2351C]' : 'text-[#232323]'}`}>{value}</span>
  </div>
)

type LtcgSummaryProps = {
  summary?: TaxHarvestingSummaryResponse
  longTermGainsSummary?: TaxHarvestingSummaryResponse['longTermGainsSummary']
}

const LtcgSummary = ({ summary, longTermGainsSummary }: LtcgSummaryProps) => {
  const equityLtcg = summary?.equityLtcg ?? 0
  const equityLtcl = summary?.equityLtcl ?? 0
  const nonEquityLtcg = summary?.nonEquityLtcg ?? 0
  const nonEquityLtcl = summary?.nonEquityLtcl ?? 0
  const totalLtcl = Math.abs(equityLtcl + nonEquityLtcl)
  const netEquityLtcg = equityLtcg - equityLtcl
  const netNonEquityLtcg = nonEquityLtcg - nonEquityLtcl

  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`

  return (
    <div className='text-[14px] text-[#6A6E6E] leading-[150%] pb-4 flex flex-col gap-4'>
      <div className='border border-[#EBF2F2] rounded-2xl px-4 py-2 bg-white'>
        <LtcgSectionRow label='Equity LTCG' value={format(equityLtcg)} />
        <LtcgSectionRow label='Non-equity LTCG' value={format(nonEquityLtcg)} />
      </div>
      <div className='border border-[#EBF2F2] rounded-2xl px-4 py-2 bg-white'>
        <LtcgSectionRow
          label='Equity + Non-equity LTCL'
          value={`${totalLtcl !== 0 ? '-' : ''} ${format(totalLtcl)}`}
          highlight
        />
      </div>
      <div className='bg-[#E6F2FF] rounded-xl'>
        <div
          role={GROUPED_STATIC_TEXT_ROLE}
          className='px-4 py-4 flex items-center justify-between border-b border-[#004D991A]'
          aria-label={`Net realised LTCG, ${format(summary?.netRealisedLtcg ?? 0)}`}
        >
          <span aria-hidden='true' className='font-medium text-[#232323]'>Net realised LTCG</span>
          <span aria-hidden='true' className='font-medium text-[#232323]'>{format(summary?.netRealisedLtcg ?? 0)}</span>
        </div>
        <div className='border border-[#EBF2F2] rounded-2xl px-4 py-2'>
          <LtcgSectionRow label='Net Equity LTCG' value={format((longTermGainsSummary?.equityLtcg ?? 0) - ((longTermGainsSummary?.equityLtcl ?? 0) + (longTermGainsSummary?.nonEquityLtcl ?? 0)) + (longTermGainsSummary?.ltclOffsettingNeqLtcg ?? 0))} />
          <LtcgSectionRow label='Net Non-equity LTCG' value={format((longTermGainsSummary?.nonEquityLtcg ?? 0) - (longTermGainsSummary?.ltclOffsettingNeqLtcg ?? 0))} />
        </div>
      </div>
      <div className='text-sm'>LTCL is used to offset non-equity LTCG first.</div>
    </div>
  )
}

type LtcgTaxLiabilityProps = {
  summary?: TaxHarvestingSummaryResponse
  longTermGainsSummary?: TaxHarvestingSummaryResponse['longTermGainsSummary']
}

const LtcgTaxLiability = ({ summary, longTermGainsSummary }: LtcgTaxLiabilityProps) => {
  const { openNestedBottomSheet, closeNestedBottomSheet } = useBottomSheet()
  const taxLiabilityLtcg = summary?.taxLiabilityLtcg ?? 0
  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`

  const openStclInfoSheet = () => {
    trackTaxHarvestingUserClicked({
      screen: TaxHarvestingScreenName.LtcgTaxLiability,
      eventId: TaxHarvestingEventId.RealisedStclInfo
    })
    openNestedBottomSheet(
      <RealisedStclInfoSheet onDismiss={closeNestedBottomSheet} />,
      closeNestedBottomSheet
    )
  }

  return (
    <div className='text-[14px] text-[#6A6E6E] leading-[150%] pb-4 flex flex-col gap-4'>
      {/* Plan highlights (including NoTaxSavings) are only shown on the Plan page, not inside this generic LTCG bottom sheet */}
      <EquityLtcgTaxLiability
        longTermGainsSummary={longTermGainsSummary}
        onRealisedStclClick={openStclInfoSheet}
        summary={summary}
        onExemptionClick={() => {
          trackTaxHarvestingUserClicked({
            screen: TaxHarvestingScreenName.LtcgTaxLiability,
            eventId: TaxHarvestingEventId.ExemptionUsedInfo
          })
          openNestedBottomSheet(
            <LtcgExemptionInfoSheet
              totalExemption={summary?.totalExemption ?? 0}
              usedExemption={summary?.usedExemption ?? 0}
              onDismiss={() => {
                closeNestedBottomSheet()
              }}
            />,
            () => {
              closeNestedBottomSheet()
            }
          )
        }}
        showPlanHighlights={false}
      />
      <NonEquityLtcgTaxLiability
        summary={summary}
        onRealisedStclClick={openStclInfoSheet}
        showPlanHighlights={false}
        longTermGainsSummary={longTermGainsSummary}
      />
      <div
        role={GROUPED_STATIC_TEXT_ROLE}
        className='mt-3 rounded-xl bg-[#E6F1FF] px-4 py-4 flex items-center justify-between'
        aria-label={`Total tax liability, ${format(taxLiabilityLtcg)}`}
      >
        <span aria-hidden='true' className='text-[#232323] font-medium'>Total tax liability</span>
        <span aria-hidden='true' className='font-medium text-[#232323]'>{format(taxLiabilityLtcg)}</span>
      </div>
    </div>
  )
}

type EquityLtcgTaxLiabilityProps = {
  summary?: TaxHarvestingSummaryResponse
  onExemptionClick?: () => void
  onRealisedStclClick?: () => void
  /** When true, show plan-specific green info boxes (used only on plan page) */
  showPlanHighlights?: boolean
  /** Possible tax saving from the equity LTCG plan, if available */
  equityPlanPossibleTaxSave?: number
  /** When false, indicates there is no equity LTCG plan available (used to show NoTaxSavings) */
  hasEquityLtcgPlan?: boolean
  /** Tax-free book amount for the equity LTCG plan, when available */
  equityPlanTaxFreeBookAmount?: number
  /** Gain book amount for the equity LTCG plan, when available */
  equityPlanGainBookAmount?: number
  longTermGainsSummary?: TaxHarvestingSummaryResponse['longTermGainsSummary']
}

export const EquityLtcgTaxLiability = ({
  summary,
  onExemptionClick,
  onRealisedStclClick,
  showPlanHighlights,
  equityPlanPossibleTaxSave,
  hasEquityLtcgPlan,
  equityPlanTaxFreeBookAmount,
  equityPlanGainBookAmount,
  longTermGainsSummary
}: EquityLtcgTaxLiabilityProps) => {
  const totalExemption = summary?.totalExemption ?? 0
  const usedExemption = summary?.usedExemption ?? 0
  const equityLtcgRate = summary?.equityLtcgRate ?? 12.5
  const netEquityLtcg = (summary?.equityLtcg ?? 0) - (summary?.equityLtcl ?? 0)
  const taxLiabilityEquityLtcg = summary?.taxLiabilityEquityLtcg ?? 0
  const taxableLtcg = netEquityLtcg - usedExemption
  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`
  const remainingExemption = Math.max(totalExemption - usedExemption, 0)
  const hasExemptionOpportunity = totalExemption > 0 && remainingExemption > 0
  const hasTaxSavingOpportunity = (equityPlanPossibleTaxSave ?? 0) > 0
  const isNetEquityLtcgNegative = netEquityLtcg < 0

  const ltgsNetEquity = (longTermGainsSummary?.equityLtcg ?? 0) - ((longTermGainsSummary?.equityLtcl ?? 0) + (longTermGainsSummary?.nonEquityLtcl ?? 0)) + (longTermGainsSummary?.ltclOffsettingNeqLtcg ?? 0)
  const ltgsRemainingStcg = longTermGainsSummary?.stclOffsettingEqLtcg ?? 0
  const ltgsTaxableLtcg = ltgsNetEquity - usedExemption - ltgsRemainingStcg
  const ltgsTaxLiability = ltgsTaxableLtcg > 0 ? ltgsTaxableLtcg * equityLtcgRate / 100 : 0

  return (
    <div className='flex flex-col gap-3'>
      <div className='border border-[#EBF2F2] rounded-2xl pt-3 pb-[1px] px-1 bg-white'>
        <div className='px-3 mb-4'>
          <LtcgSectionRow label='Net Equity LTCG' value={format(ltgsNetEquity)} />
          {ltgsRemainingStcg > 0 ? (
            <LtcgSectionRow
              label='Remaining STCL'
              value={`-${format(ltgsRemainingStcg)}`}
              onClick={onRealisedStclClick}
              showInfo
            />
          ) : null}
          <LtcgSectionRow
            label='Exemption used'
            value={`${format(usedExemption)} / ${format(totalExemption)}`}
            onClick={onExemptionClick}
            showInfo
          />
          {ltgsTaxableLtcg > 0 && <LtcgSectionRow label='Taxable LTCG' value={format(ltgsTaxableLtcg)} />}
          <LtcgSectionRow label={`Tax @ ${equityLtcgRate}%`} value={format(ltgsTaxLiability)} />
        </div>
        {showPlanHighlights && isNetEquityLtcgNegative && totalExemption > 0 && hasEquityLtcgPlan && (
          <div className='rounded-2xl bg-gold90 px-4 py-3'>
            <div className='text-[15px] font-medium text-orange10'>
              {`${roundOffAndFormatNumberWithCurrency(equityPlanTaxFreeBookAmount ?? 0)} unused exemption available`}
            </div>
            <div className='mt-2 text-[13px] text-orange10 opacity-60 leading-[1.5]'>
              You have {roundOffAndFormatNumberWithCurrency(netEquityLtcg)} equity LTCG in your portfolio which you can book to save {format(equityPlanPossibleTaxSave ?? 0)} in future tax.
              {/* Up to ₹1.25L equity long-term gains are tax-free every FY. Also use your {format(netEquityLtcg ?? 0)} losses to reduce taxable gains further, saving {format(equityPlanPossibleTaxSave ?? 0)} in tax overall */}
              {/* Upto ₹1.25L LTCG gains is exempted every FY and you can also offset losses with available
              gains (LTCG). Hence you can withdraw total of{' '}
              {roundOffAndFormatNumberWithCurrency(totalGainsTaxFreeWhenNegative)} gains tax-free. */}
            </div>
          </div>
        )}

        {showPlanHighlights && hasExemptionOpportunity && !isNetEquityLtcgNegative && hasEquityLtcgPlan && (
          <div className='rounded-2xl bg-gold90 px-4 py-3'>
            <div className='text-sm font-medium text-orange10'>
              {`${roundOffAndFormatNumberWithCurrency(totalExemption - usedExemption + (usedExemption === 0 ? ((longTermGainsSummary?.stclOffsettingEqLtcg ?? 0) - ltgsNetEquity) : 0))} exemption limit can be used`}
            </div>
            <div className='mt-1 text-13 text-orange10'>
              You have {roundOffAndFormatNumberWithCurrency(equityPlanTaxFreeBookAmount ?? summary?.taxFreeBookAmount ?? 0)} equity LTCG in your portfolio which you can book to save {format(equityPlanPossibleTaxSave ?? 0)} in future tax.
              {/* Upto ₹1.25L equity long-term gains are tax-free every financial year, saving you {format(equityPlanPossibleTaxSave ?? 0)} in tax */}
            </div>
          </div>
        )}

        {showPlanHighlights &&
          hasTaxSavingOpportunity &&
          !hasExemptionOpportunity &&
          ((summary?.taxFreeBookAmount ?? 0) > 0 ||
            (equityPlanGainBookAmount ?? 0) > 0) &&
          hasEquityLtcgPlan && (
            <div className='rounded-2xl bg-gold90 px-4 py-3'>
              <div className='text-sm font-medium text-orange10'>
                Save <span className='font-semibold'>{format(equityPlanPossibleTaxSave ?? 0)}</span> in tax
              </div>
              <div className='mt-1 text-13 text-orange10 opacity-60'>
                Reduce your taxable gains by booking losses to lower your tax liability
              </div>
            </div>
          )}
        {showPlanHighlights && hasEquityLtcgPlan === false && (
          <div className='mt-3'>
            <NoTaxSavings
              gainType={GainType.LTCG}
              taxLiability={format(taxLiabilityEquityLtcg)}
              showChevron={false}
            />
          </div>
        )}
      </div>
    </div>
  )
}

type NonEquityLtcgTaxLiabilityProps = {
  summary?: TaxHarvestingSummaryResponse
  onRealisedStclClick?: () => void
  /** When true, show plan-specific green info boxes (used only on plan page) */
  showPlanHighlights?: boolean
  /** Possible tax saving from the non-equity LTCG plan, if available */
  nonEquityPlanPossibleTaxSave?: number
  longTermGainsSummary?: TaxHarvestingSummaryResponse['longTermGainsSummary']
}

export const NonEquityLtcgTaxLiability = ({
  summary,
  onRealisedStclClick,
  showPlanHighlights,
  nonEquityPlanPossibleTaxSave,
  longTermGainsSummary
}: NonEquityLtcgTaxLiabilityProps) => {
  const nonEquityLtcgRate = summary?.nonEquityLtcgRate ?? 12.5
  const netNonEquityLtcg = (summary?.nonEquityLtcg ?? 0) - (summary?.nonEquityLtcl ?? 0)
  const taxLiabilityNonEquityLtcg = summary?.taxLiabilityNonEquityLtcg ?? 0
  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`
  const hasTaxSavingOpportunity = (nonEquityPlanPossibleTaxSave ?? 0) > 0

  return (
    <div className='flex flex-col gap-3'>
      <div className='border border-[#EBF2F2] rounded-2xl pt-3 pb-[1px] px-1 bg-white'>
        <div className='px-3 mb-4'>
          <LtcgSectionRow label='Net Non-equity LTCG' value={format((longTermGainsSummary?.nonEquityLtcg ?? 0) - (longTermGainsSummary?.ltclOffsettingNeqLtcg ?? 0))} />
          {longTermGainsSummary?.stclOffsettingNeqLtcg ? (
            <LtcgSectionRow
              label='Remaining STCL'
              value={`-${format(longTermGainsSummary?.stclOffsettingNeqLtcg ?? 0)}`}
              onClick={onRealisedStclClick}
              showInfo
            />
          ) : null}
          <LtcgSectionRow label={`Tax @ ${nonEquityLtcgRate}%`} value={format(taxLiabilityNonEquityLtcg)} />
        </div>
        {showPlanHighlights &&
          (hasTaxSavingOpportunity ? (
            <div className='rounded-2xl bg-gold90 px-4 py-3'>
              <div className='text-sm font-medium text-orange10'>
                Save <span className='font-semibold'>{format(nonEquityPlanPossibleTaxSave ?? 0)}</span> in tax
              </div>
              <div className='mt-1 text-13 text-orange10 opacity-60'>
                Reduce your taxable gains by booking losses to lower your tax liability
              </div>
            </div>
          ) : (
            <NoTaxSavings gainType={GainType.LTCG} taxLiability={format(taxLiabilityNonEquityLtcg)} showChevron={false} />
          ))}
      </div>
    </div>
  )
}

type LtcgExemptionInfoSheetProps = {
  totalExemption: number
  usedExemption: number
  onDismiss: () => void
}

export function LtcgExemptionInfoSheet({
  totalExemption,
  usedExemption,
  onDismiss
}: LtcgExemptionInfoSheetProps) {
  useEffect(() => {
    trackTaxHarvestingBottomSheetLoaded(TaxHarvestingBottomSheetName.ExemptionLimit)
  }, [])
  const format = (value: number) => `${value < 0 ? '-' : ''} ${roundOffAndFormatNumberWithCurrency(value)}`
  const remaining = Math.max(totalExemption - usedExemption, 0)

  return (
    <BottomSheetContent
      heading='Exemption limit'
      bottomSheetHeadingClassName='!mb-2'
      logoSvg={null}
      onDismiss={onDismiss}
    >
      <div className='pb-2'>
        <p className='text-[14px] text-[#6A6E6E] leading-[150%]'>
          You can book upto ₹1.25L equity long-term gains tax-free every year. Any gains above this limit are taxed at 12.5%.
          Utilising this limit every year can save you ₹15,625 in tax annually.
        </p>

        <div className='mt-4 border border-[#EBF2F2] rounded-2xl bg-white px-4 py-3 flex flex-col gap-2'>
          <div
            role={GROUPED_STATIC_TEXT_ROLE}
            className='flex itemscenter justify-between text-[13px] text-[#6A6E6E]'
            aria-label={`Exemption used, ${format(usedExemption)} / ${format(totalExemption)}`}
          >
            <span aria-hidden='true'>Exemption used</span>
            <span aria-hidden='true' className='font-medium text-[#232323]'>
              {format(usedExemption)} / {format(totalExemption)}
            </span>
          </div>
          <div
            role={GROUPED_STATIC_TEXT_ROLE}
            className='flex items-center justify-between text-[13px] text-[#6A6E6E]'
            aria-label={`Limit remaining, ${format(remaining)}`}
          >
            <span aria-hidden='true'>Limit remaining</span>
            <span aria-hidden='true' className='font-medium text-[#232323]'>{format(remaining)}</span>
          </div>
          <div className='mt-2 h-[3px] w-full rounded-full bg-[#E6F6EC] overflow-hidden'>
            <div
              className='h-full rounded-full bg-[#0B6B2A]'
              style={{
                width:
                  totalExemption > 0
                    ? `${Math.min(100, (Math.max(usedExemption, 0) / totalExemption) * 100)}%`
                    : '0%'
              }}
            />
          </div>
        </div>
      </div>
    </BottomSheetContent>
  )
}

